import React, { useState } from 'react';
import {
  Box, Text, Stack, Group, Button, Badge, Code, Loader, Alert, List,
  Collapse, Textarea
} from '@mantine/core';
import {
  IconAlertCircle, IconDownload, IconReload, IconCircleCheck,
  IconShieldCheck, IconTool, IconAlertTriangle,
  IconGavel, IconArrowBackUp
} from '@tabler/icons-react';
import { submitFullBuildRetry } from '../api_scripts/agentStudioClient.js';

/**
 * Step 4 — generation progress + downloads.
 *
 * onRetryFromImportError is called with the retry submission response
 * ({ jobId, priorJobId, status }) after a successful retry submission so the
 * parent wizard can update its jobId state and start polling the new retry job.
 */
function GenerationProgressStep({ jobId, jobStatus, onRetry, onRetryFromImportError, getApiToken }) {
  if (!jobId) {
    return <Text c="dimmed">Click Generate to start.</Text>;
  }

  const status = jobStatus?.status || 'PENDING';
  const steps = jobStatus?.batchMetadata?.completedSteps || [];
  const isDone = status === 'SUCCEEDED';
  const isFailed = status === 'FAILED';

  return (
    <Stack gap="md">
      <Group>
        <Text fw={600}>Job:</Text>
        <Code>{jobId}</Code>
        <Badge
          color={isDone ? 'green' : isFailed ? 'red' : 'cyan'}
          variant="filled"
          leftSection={isDone ? <IconCircleCheck size={12} /> : null}
        >
          {status}
        </Badge>
      </Group>

      {!isDone && !isFailed && (
        <Group>
          <Loader size="sm" color="cyan" />
          <Text size="sm" c="dimmed">Working through the pipeline…</Text>
        </Group>
      )}

      {steps.length > 0 && (
        <Box>
          <Text size="sm" fw={500} mb={4}>Pipeline progress</Text>
          <Stack gap={4}>
            {steps.map((s, i) => (
              <Group key={i} gap="xs">
                <IconCircleCheck size={14} color="#2f9e44" />
                <Text size="sm">{s}</Text>
              </Group>
            ))}
          </Stack>
        </Box>
      )}

      {isFailed && (
        <Alert icon={<IconAlertCircle size={16} />} color="red">
          {jobStatus.error?.message || 'Generation failed'}
          <Group mt="sm">
            <Button leftSection={<IconReload size={14} />} variant="light" color="red" size="xs" onClick={onRetry}>
              Retry
            </Button>
          </Group>
        </Alert>
      )}

      {isDone && jobStatus.result && (
        <Box>
          <Text fw={600} mb="sm">Downloads</Text>
          <Stack gap="xs">
            {(jobStatus.result.bots || []).map((b, i) => (
              <Group key={i}>
                <Text size="sm" style={{ flex: 1 }}>{b.name}.json</Text>
                <Button
                  size="xs"
                  variant="subtle"
                  color="cyan"
                  leftSection={<IconDownload size={14} />}
                  onClick={() => downloadJson(b.json, `${b.name}.json`)}
                >
                  Download
                </Button>
              </Group>
            ))}
            {jobStatus.result.flow && (
              <Group>
                <Text size="sm" style={{ flex: 1 }} fw={600}>{jobStatus.result.flow.name || 'flow'}.json</Text>
                <Button
                  size="xs"
                  variant="filled"
                  color="cyan"
                  leftSection={<IconDownload size={14} />}
                  onClick={() => downloadJson(jobStatus.result.flow, `${jobStatus.result.flow.name || 'flow'}.json`)}
                >
                  Download flow
                </Button>
              </Group>
            )}
          </Stack>

          <ValidationReport report={jobStatus.result.validationReport} />
          <JudgeReport judge={jobStatus.result.judgeResult} />
          <FailPathPanel
            jobId={jobId}
            onSubmitted={onRetryFromImportError}
            getApiToken={getApiToken}
          />
        </Box>
      )}
    </Stack>
  );
}

// C2: surface the synthesizer's validation preflight results. The report
// carries auto-repairs applied (e.g. Case default port auto-wired) and any
// warnings the engineer should review before importing to Studio.
function ValidationReport({ report }) {
  if (!report) return null;
  const errors = (report.warnings || []).filter((w) => w.severity === 'error');
  const warns = (report.warnings || []).filter((w) => w.severity === 'warn');
  const infos = (report.warnings || []).filter((w) => w.severity === 'info');
  const repaired = report.repaired || [];
  const nothingToShow =
    errors.length === 0 && warns.length === 0 && infos.length === 0 && repaired.length === 0;
  if (nothingToShow) return null;

  return (
    <Stack gap="sm" mt="md">
      <Group gap="xs">
        <IconShieldCheck size={16} color={report.passed ? '#2f9e44' : '#e8590c'} />
        <Text size="sm" fw={600}>
          Studio validation preflight — {report.passed ? 'passed' : 'issues to review'}
        </Text>
      </Group>

      {repaired.length > 0 && (
        <Alert icon={<IconTool size={16} />} color="cyan" variant="light">
          <Text size="xs" fw={600} mb={4}>Auto-repairs applied ({repaired.length})</Text>
          <List size="xs" spacing={2}>
            {repaired.map((r, i) => (
              <List.Item key={i}>
                {r.activity ? <Code>{r.activity}</Code> : null} {r.message}
              </List.Item>
            ))}
          </List>
        </Alert>
      )}

      {errors.length > 0 && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
          <Text size="xs" fw={600} mb={4}>Errors ({errors.length}) — Studio will reject on import</Text>
          <List size="xs" spacing={2}>
            {errors.map((w, i) => (
              <List.Item key={i}>
                <Badge size="xs" color="red" variant="light">{w.code}</Badge>{' '}
                {w.activity ? <Code>{w.activity}</Code> : null} {w.message}
              </List.Item>
            ))}
          </List>
        </Alert>
      )}

      {warns.length > 0 && (
        <Alert icon={<IconAlertTriangle size={16} />} color="yellow" variant="light">
          <Text size="xs" fw={600} mb={4}>Warnings ({warns.length})</Text>
          <List size="xs" spacing={2}>
            {warns.map((w, i) => (
              <List.Item key={i}>
                <Badge size="xs" color="yellow" variant="light">{w.code}</Badge>{' '}
                {w.activity ? <Code>{w.activity}</Code> : null} {w.message}
              </List.Item>
            ))}
          </List>
        </Alert>
      )}

      {infos.length > 0 && (
        <Alert color="gray" variant="light">
          <Text size="xs" fw={600} mb={4}>Notices ({infos.length})</Text>
          <List size="xs" spacing={2}>
            {infos.map((w, i) => (
              <List.Item key={i}>
                <Badge size="xs" color="gray" variant="light">{w.code}</Badge> {w.message}
              </List.Item>
            ))}
          </List>
        </Alert>
      )}
    </Stack>
  );
}

// D1: LLM Flow-Judge findings display. Renders the overallScore badge (green
// ≥0.8, yellow ≥0.6, red <0.6) and the collapsible per-item finding list.
function JudgeReport({ judge }) {
  const [open, setOpen] = useState(false);
  if (!judge) return null;
  const score = typeof judge.overallScore === 'number' ? judge.overallScore : null;
  const findings = Array.isArray(judge.perItem) ? judge.perItem : [];
  const color = score == null ? 'gray' : score >= 0.8 ? 'green' : score >= 0.6 ? 'yellow' : 'red';

  return (
    <Stack gap="xs" mt="md">
      <Group gap="xs">
        <IconGavel
          size={16}
          color={color === 'green' ? '#2f9e44' : color === 'yellow' ? '#f08c00' : color === 'red' ? '#e03131' : '#868e96'}
        />
        <Text size="sm" fw={600}>LLM Flow-Judge review</Text>
        {score != null && (
          <Badge size="sm" color={color} variant="light">
            {(score * 100).toFixed(0)}% quality
          </Badge>
        )}
        {findings.length > 0 && (
          <Button size="xs" variant="subtle" onClick={() => setOpen(!open)}>
            {open ? 'Hide' : 'Show'} {findings.length} finding{findings.length === 1 ? '' : 's'}
          </Button>
        )}
      </Group>
      <Collapse in={open}>
        <Stack gap="xs">
          {findings.map((f, i) => (
            <Alert
              key={i}
              variant="light"
              color={f.severity === 'error' ? 'red' : f.severity === 'warn' ? 'yellow' : 'gray'}
            >
              <Group gap="xs" mb={4}>
                <Badge
                  size="xs"
                  color={f.severity === 'error' ? 'red' : f.severity === 'warn' ? 'yellow' : 'gray'}
                  variant="filled"
                >
                  {f.severity}
                </Badge>
                <Code>{f.itemId}</Code>
              </Group>
              <Text size="xs" mb={4}><strong>Reason:</strong> {f.reason}</Text>
              {f.suggestion && (
                <Text size="xs" c="dimmed"><strong>Suggested fix:</strong> {f.suggestion}</Text>
              )}
            </Alert>
          ))}
        </Stack>
      </Collapse>
    </Stack>
  );
}

// D2: fail-path panel. Engineer imports the flow into Studio; if it fails,
// paste the error text here and click Retry. The backend re-runs DSL → synth
// with the errors as priorJudgeFindings, reusing the same bots + tenant so
// they don't have to redo the whole PDF classification pipeline.
function FailPathPanel({ jobId, onSubmitted, getApiToken }) {
  const [open, setOpen] = useState(false);
  const [errorText, setErrorText] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  async function handleRetry() {
    if (!errorText.trim()) {
      setSubmitError('Paste the Studio error text first.');
      return;
    }
    setSubmitError(null);
    setSubmitting(true);
    try {
      const resp = await submitFullBuildRetry({
        priorJobId: jobId,
        importErrors: errorText.trim(),
        note: note.trim() || undefined
      }, getApiToken);
      setErrorText('');
      setNote('');
      setOpen(false);
      if (typeof onSubmitted === 'function') {
        onSubmitted(resp);
      }
    } catch (err) {
      setSubmitError(err?.message || 'Retry submission failed');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Stack gap="xs" mt="md">
      <Group gap="xs">
        <IconArrowBackUp size={16} />
        <Text size="sm" fw={600}>Studio import failed?</Text>
        <Button size="xs" variant="subtle" onClick={() => setOpen(!open)}>
          {open ? 'Cancel' : 'Retry with error text'}
        </Button>
      </Group>
      <Collapse in={open}>
        <Stack gap="xs">
          <Text size="xs" c="dimmed">
            Paste the exact error message from WxCC Studio's validation panel
            (e.g. <Code>FC1015: Field not configured</Code>). We'll re-run the DSL
            generator + synthesizer against the same bots + tenant template with
            the error as a constraint, so you don't have to redo the PDF upload.
          </Text>
          <Textarea
            label="Studio import error"
            placeholder="FC1015: Field not configured correctly on activity SV_HandWrist. srcVariable 'body_part' is not declared in flow.variables."
            value={errorText}
            onChange={(e) => setErrorText(e.currentTarget.value)}
            minRows={3}
            autosize
            maxRows={10}
            disabled={submitting}
          />
          <Textarea
            label="Additional context (optional)"
            placeholder="e.g. only fires on the knee branch after last_two_weeks=no"
            value={note}
            onChange={(e) => setNote(e.currentTarget.value)}
            minRows={2}
            autosize
            maxRows={5}
            disabled={submitting}
          />
          {submitError && (
            <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
              {submitError}
            </Alert>
          )}
          <Group>
            <Button
              size="xs"
              color="cyan"
              leftSection={<IconArrowBackUp size={14} />}
              onClick={handleRetry}
              loading={submitting}
            >
              Submit retry
            </Button>
          </Group>
        </Stack>
      </Collapse>
    </Stack>
  );
}

function downloadJson(obj, filename) {
  const blob = new Blob([JSON.stringify(obj, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

export default GenerationProgressStep;
